// A selection requests a runner job. Only the session feed's confirmed
// settings replace current values or dependent choices; API acceptance alone
// never does. This component remounts when its session/person scope changes.
import { useEffect, useInsertionEffect, useRef, useState } from 'react'
import type { CyberAccessProgram, ConfigChoice, Runner, Session } from '../api/types'
import { ApiError, errorMessage } from '../api/client'
import { uuid4 } from '../api/uuid'
import { toast } from '../components/toasts'
import { useStore, useBoardState } from '../store/context'
import { agentOf } from './format'
import { Picker } from './ChoiceSelect'
import { choices, concrete, currentEffort, effortOption, modelChoices, modelEffort, modeChoices, startupEffort } from './choices'
import { modelVariants, modelVariantKey, retiredCodexModel } from './modelVariants'
import './controls.css'

// This bounds local observation only; it does not cancel or fail the runner job.
const SETTINGS_OBSERVATION_WAIT_MS = 30_000
type Accepted = {job: string; previousJobs: string[]; deadline: number}

export function SessionControls({session, disabled, runner}: {session: Session; disabled: boolean; runner?: Runner}) {
  const {me} = useBoardState()
  return <BoundControls key={JSON.stringify([me?.workspace.id, me?.member?.id, session.id])} session={session} disabled={disabled} runner={runner}/>
}
function BoundControls({session, disabled, runner}: {session: Session; disabled: boolean; runner?: Runner}) {
  const store = useStore()
  const mounted = useRef(true)
  useInsertionEffect(() => () => {mounted.current = false}, [])
  const {me, offline} = useBoardState()
  const [sending, setSending] = useState(false)
  const busy = useRef(false)
  const [accepted, setAccepted] = useState<Accepted | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const [unknownJob, setUnknownJob] = useState<string | null>(null)
  const settings = session.settings
  const [repairing, setRepairing] = useState(false)
  useEffect(() => {
    const change = settings?.change
    if (change?.state === 'refused' || change?.state === 'unconfirmed') setRepairing(true)
    else if (!accepted && change?.state === 'confirmed') setRepairing(false)
  }, [accepted, settings])
  useEffect(() => {
    if (!accepted) return
    const change = settings?.change
    if (session.state === 'ended') {setAccepted(null);setUncertain(true)}
    else if (change?.job === accepted.job && !['pending', 'applying'].includes(change.state)) {setAccepted(null);if (change.state === 'confirmed') setRepairing(false)}
    else if (change && change.job !== accepted.job && !accepted.previousJobs.includes(change.job)) {
      // A latest-only snapshot can supersede this request. Its outcome is
      // unknown; the current snapshot remains authoritative, never our draft.
      setAccepted(null)
      setUnknownJob(accepted.job)
      setUncertain(true)
      if (change.state === 'confirmed') setRepairing(false)
    }
  }, [accepted, settings, session.state])
  useEffect(() => {
    if (!accepted) return
    // Effects pause while Activity is hidden. The absolute deadline survives it.
    const timer = setTimeout(() => {
      setAccepted(null)
      setUnknownJob(accepted.job)
      setUncertain(true)
    }, Math.max(0, accepted.deadline - Date.now()))
    return () => clearTimeout(timer)
  }, [accepted])
  useEffect(() => {
    const change = settings?.change
    if (!accepted && !sending && unknownJob && change?.job === unknownJob && !['pending', 'applying'].includes(change.state)) {
      setUnknownJob(null)
      setUncertain(false)
      if (change.state === 'confirmed') setRepairing(false)
    }
  }, [accepted, sending, unknownJob, settings])
  if (!settings || !me?.member || session.origin.kind === 'review') return null
  const state = settings.change?.state
  const awaitingJob = accepted !== null && (settings.change?.job !== accepted.job || state === 'pending' || state === 'applying')
  const waiting = sending || awaitingJob || state === 'pending' || state === 'applying'
  const locked = disabled || offline || waiting
  const requiresChoice = uncertain || repairing || state === 'refused' || state === 'unconfirmed'
  const agent = agentOf(session, runner ? [runner] : [])
  const adapter = agent?.agent
  const models = modelChoices(settings).filter(model => session.agent.vendor !== 'openai' || !retiredCodexModel(model.id))
  const program = settings.options.find(option => option.category === 'access_program')?.current_value as CyberAccessProgram | undefined
  const variants = modelVariants(models, settings.access_programs, settings.current_model, program)
  const effort = effortOption(settings.options, adapter)
  const efforts = choices(effort?.choices)
  const current = currentEffort(settings, adapter)
  const modes = modeChoices(settings.modes)
  const identity = JSON.stringify([me.workspace.id, me.member.id])
  const sameScope = () => {const now = store.getState();return now.status !== 'signed_out' && JSON.stringify([now.me?.workspace.id, now.me?.member?.id]) === identity}
  const change = (selection: {model?: string; mode?: string; effort?: ConfigChoice; cyber_access_program?: CyberAccessProgram | null}) => {
    if (busy.current || locked || !sameScope() || store.getState().offline) return
    busy.current = true
    // Only the previous projection and one locally unknown job can be replayed
    // across this repair. Neither can settle the new request.
    const previousJobs = [settings.change?.job, unknownJob].filter((job): job is string => typeof job === 'string')
    setUnknownJob(null)
    setUncertain(false)
    setSending(true)
    store.sendSessionSettings({mutation_id: uuid4(), op: 'set_session_settings', args: {session_id: session.id, expected_revision: settings.revision, ...selection}})
      .then(answer => {if (mounted.current && sameScope()) setAccepted({job: answer.job, previousJobs, deadline: Date.now() + SETTINGS_OBSERVATION_WAIT_MS})})
      .catch((error: unknown) => {if (mounted.current && sameScope()) {toast(`Could not change settings: ${errorMessage(error)}`, 'error');if (error instanceof ApiError && error.status === 409) void store.catchUp()}})
      .finally(() => {busy.current = false;if (mounted.current && sameScope()) setSending(false)})
  }
  if (session.state === 'ended') return uncertain ? <span className="text-danger" role="alert">The settings change could not be confirmed before the session ended.</span> : null
  return <div className="code-pickers" aria-label="Session settings" aria-busy={waiting}>
    {models.length > 0 && <Picker label="Model" value={!requiresChoice && concrete(settings.current_model) ? modelVariantKey(settings.current_model, program, !!program && !settings.access_programs?.[settings.current_model]?.includes(program)) : null} choices={variants} disabled={locked} onChange={value => {
      const choice = variants.find(choice => choice.id === value)
      if (choice) {
        const effort = modelEffort(startupEffort(agent, choice.modelId), current)
        change({model: choice.modelId, ...(effort ? {effort} : {}), ...(settings.access_programs ? {cyber_access_program: choice.selectionProgram ?? null} : {})})
      }
    }}/>}
    {effort && <Picker label="Effort" value={requiresChoice ? null : current?.value ?? null} choices={efforts} disabled={locked || efforts.length === 0} missing={efforts.length === 0 ? 'Not reported' : current || requiresChoice ? undefined : 'Default'} onChange={value => change({effort: {id: effort.id, value}})}/>}
    {session.place?.kind !== 'checkout' && modes.length > 0 && <Picker label="Mode" value={requiresChoice ? null : settings.current_mode} choices={modes} disabled={locked} onChange={mode => change({mode})}/>}
    {waiting && <span className="code-picker-name" role="status">Changing settings…</span>}
    {uncertain && <span className="text-danger" role="alert">The earlier settings change could not be confirmed.{!waiting && ' Choose again.'}</span>}
    {(state === 'refused' || state === 'unconfirmed') && <span className="text-danger" role="alert">{state === 'refused' ? 'The settings change was refused. Choose again.' : 'The settings change could not be confirmed. Choose again.'}</span>}
  </div>
}
